import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesDynamicsService } from '../../../../services/CoursesDynamics/CoursesDynamicsService'
import { CoursesDynamicsModal } from './CoursesDynamicsModal'

const CoursesDynamicsPage = () => {
  const service = new CoursesDynamicsService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const permissions = {
    addCourseFilter: hasRole('add_course_filter'),
    deleteCourseFilter: hasRole('delete_course_filter')
  }

  const [dynamics, setDynamics] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [search, setSearch] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDynamic, setEditingDynamic] = useState<ICourseInterface | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const fetchDynamics = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.list()
      setDynamics(data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as dinâmicas', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setFiltered(dynamics.filter((dynamic) => dynamic.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, dynamics])

  useEffect(() => {
    fetchDynamics()
  }, [])

  function openEditModal(dynamic: ICourseInterface) {
    setEditingDynamic(dynamic)
    setIsModalOpen(true)
  }

  function openCreateModal() {
    setEditingDynamic(null)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingDynamic(null)
  }

  function openDeleteDialog(id: number) {
    setIdDeleting(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  async function handleDelete() {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast.success('Dinâmica excluída com sucesso')
      fetchDynamics()
    } catch {
      toast.error('Erro ao excluir dinâmica')
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <>
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por dinâmica"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="dinâmicas" feminine onRetry={fetchDynamics} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="dinâmica" feminine />
            ) : (
              filtered.map((dynamic) => (
                <Tr key={dynamic.id}>
                  <Td>{dynamic.id}</Td>
                  <Td>{dynamic.name}</Td>
                  <Td>{formatDateTime(dynamic.created_at)}</Td>
                  <Td>
                    {permissions.deleteCourseFilter && (
                      <ActionButton action="edit" onClick={() => openEditModal(dynamic)} />
                    )}
                    {permissions.deleteCourseFilter && (
                      <ActionButton action="delete" onClick={() => openDeleteDialog(dynamic.id)} />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {permissions.addCourseFilter && <ActionFab label="Adicionar dinâmica de ensino" onClick={openCreateModal} />}

      <CoursesDynamicsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onFinished={fetchDynamics}
        initialData={editingDynamic}
        service={service}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        action="delete"
        entity="dinâmica de ensino"
        feminine
      />
    </>
  )
}

export default CoursesDynamicsPage
